import { ClickAwayListener, TextField } from "@mui/material";
import { Autocomplete, AutocompleteChangeReason } from "@mui/material";
import { darkGrey, darkModeModalBackgroundColor } from "constants/colors";
import { BREAKPOINT_MOBILE, Z_INDEX } from "constants/constants";
import { atom, useAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { useIsDarkMode } from "store/atoms";
import styled from "styled-components/macro";
import { ProcessGroup } from "types/types";
import {
  useEventListener,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { getCustomersInToken } from "./Nav/topBarUtils";

export const isQuickCustomerSwitcherOpenAtom = atom(false);

// too slow with big customers. Could remove this limitation using virtualization https://material-ui.com/components/autocomplete/#virtualization
const MAX_NUM_OPTIONS_PER_CUSTOMER = 100;

const HEIGHT = 56;
const WIDTH = 400;
/** use Ctrl+K to quick-switch between customers */
const QuickCustomerSwitcher = () => {
  const [open] = useAtom(isQuickCustomerSwitcherOpenAtom);

  return open ? <QuickCustomerSwitcherContent /> : null;
};
const QuickCustomerSwitcherContent = () => {
  const visibleGroups = useVisibleGroups();
  const currentCkey = useCustomerKey();
  const [open, setOpen] = useAtom(isQuickCustomerSwitcherOpenAtom);
  let ravenEmployeeCkeys: string[] | null = null;
  try {
    ravenEmployeeCkeys = JSON.parse(
      window.localStorage.getItem("raven_employee:ckeys") ?? "[]"
    );
  } catch (error) {
    console.log("QuickCustomerSwitcher  error:", error);
  }
  const ckeys = ravenEmployeeCkeys ?? getCustomersInToken();

  type CustomerPage = { url: string; description: string };

  // array of customer page urls
  const customerPages: CustomerPage[] = ckeys.reduce((acc, ckey) => {
    let newAcc = [...acc];
    // if it's the current customer, we can fetch all the sub-pages as well
    const isCurrentCustomer = ckey === currentCkey;
    if (isCurrentCustomer) {
      const currentCustomerSubPages: CustomerPage[] = [
        {
          url: `/${ckey}/reports`,
          description: `${ckey} | Reports`,
        },
        {
          url: `/${ckey}/settings`,
          description: `${ckey} | Settings`,
        },
        {
          url: `/${ckey}/dashboard/`,
          description: `${ckey} | Group Tiles`,
        },
        ...(visibleGroups?.reduce(
          (accSubpages: CustomerPage[], group: ProcessGroup) => {
            return [
              ...accSubpages,
              // show these pages for the current customer only
              // { url: `/${ckey}/trends`, description: `Trends` },
              // tiles page: 1 for the group
              {
                url: `/${ckey}/dashboard/${group.summaryGroup}`,
                description: `${ckey} | ${group.longName} | Process Tiles`,
              },
              // timeline page: 1 for the group
              {
                url: `/${ckey}/dashboard/${group.summaryGroup}?view=tl`,
                description: `${ckey} | ${group.longName} | Timeline`,
              },
              // hublet page: 1x per process
              ...group.processes.map((process, pIdx) => ({
                url: `/${ckey}/dashboard/${group.summaryGroup}/${pIdx}`,
                description: `${ckey} | ${group.longName} | ${process.name}`,
              })),
              // // grid page: 1x per process
              // ...group.processes.map((process, pIdx) => ({
              //   url: `/${ckey}/grid/${group.summaryGroup}/${pIdx}`,
              //   description: `${group.longName} | ${process.name} | Grid`,
              // })),
              // // operator page: 1x per process
              // ...group.processes.map((process, pIdx) => ({
              //   url: `/${ckey}/operator/${group.summaryGroup}/${pIdx}`,
              //   description: `${group.longName} | ${process.name} | Operator`,
              // })),
            ] as CustomerPage[];
          },
          [] as CustomerPage[]
        ) || []),
      ];

      newAcc = [...newAcc, ...currentCustomerSubPages];
    }

    return [
      ...newAcc,
      // only show shortnames for other customers
      ...(isCurrentCustomer
        ? []
        : [{ url: ckey, description: `${ckey} | Group Tiles` }]),
      // customerTiles = "customerTiles",
      // processTiles = "processTiles",
      // groupTiles = "groupTiles",
      // gridLayout = "gridLayout",
      // operator = "operator",
      // timeline = "timeline",
      // trends = "trends",
      // hublet = "hublet",
    ];
  }, [] as CustomerPage[]);

  // type Ctrl+K or Ctrl+B to open it!
  useCtrlKorBToToggle(setOpen);

  // manually focus the input when it renders (inputProps > autoFocus not working)
  useClickOnMountToOpen(open);

  function navigateTo(customerUrl: string) {
    setOpen(false);
    const newPath = customerUrl.includes("/")
      ? customerUrl
      : `/${customerUrl}/dashboard`;
    window.location.href = `${window.location.origin}${newPath}`;
  }
  const [isDarkMode] = useIsDarkMode();

  // Controlled Autocomplete https://mui.com/components/autocomplete/#controlled-states
  const [value, setValue] = useState<CustomerPage | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const options = customerPages.reduce(
    (acc, { url, description }) => {
      const isCurrentCustomerPage = description !== "";
      // only keep the first MAX_NUM_OPTIONS_PER_CUSTOMER in the customer's list
      const keep = !isCurrentCustomerPage
        ? true
        : acc.pageIdx < MAX_NUM_OPTIONS_PER_CUSTOMER;
      return keep
        ? {
            pageIdx: acc.pageIdx + (isCurrentCustomerPage ? 1 : 0),
            options: [...acc.options, { url, description }],
          }
        : acc;
    },
    { pageIdx: 0, options: [] as CustomerPage[] }
  ).options;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <QuickCustomerSwitcherStyles $isDarkMode={isDarkMode}>
        <Autocomplete
          selectOnFocus={false}
          openOnFocus={true}
          clearOnEscape={false}
          // autoComplete={true}
          autoHighlight={true}
          renderInput={params => (
            <TextField
              {...params}
              label="Switch to any page..."
              variant="outlined"
            />
          )}
          getOptionLabel={(option: CustomerPage) => option.description}
          options={options}
          // show any options that include a fuzzy match of the text
          filterOptions={(options: CustomerPage[], { inputValue }) => {
            // keep ones that include all input words
            const filteredOptions = options.filter(option =>
              inputValue.split(" ").reduce((isMatchAcc, letter) => {
                // make sure each letter in the input is included in the option
                return isMatchAcc && option.description?.includes(letter);
              }, true as boolean)
            );
            return filteredOptions;
          }}
          onKeyUp={event => {
            // "esc" to close
            if (event.key === "Escape") {
              setOpen(false);
            }

            const inputValue = (event?.target as any)?.value;
            if (customerPages.includes(inputValue)) {
              navigateTo(inputValue);
            }
          }}
          onChange={(
            event: any,
            newValue: CustomerPage | null,
            reason: AutocompleteChangeReason
          ) => {
            if (reason !== "selectOption") {
              return;
            }
            setValue(newValue);
            if (
              newValue &&
              customerPages.map(p => p.url).includes(newValue.url)
            ) {
              navigateTo(newValue.url);
            }
          }}
          value={value}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderOption={
            // bold the matching letters
            (props, option) => {
              return (
                <OptionStyles key={option.url} {...props}>
                  <div>
                    {option.description?.split("").map((letter, idx) => (
                      <span
                        key={idx}
                        style={
                          inputValue.includes(letter)
                            ? { fontWeight: "bold" }
                            : {}
                        }
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                  <div className="description">
                    {option.url?.split("").map((letter, idx) =>
                      idx === 0 && letter === "/" ? null : (
                        <span
                          key={idx}
                          style={
                            inputValue.includes(letter)
                              ? { fontWeight: "bold" }
                              : {}
                          }
                        >
                          {letter}
                        </span>
                      )
                    )}
                  </div>
                </OptionStyles>
              );
            }
          }
        />
      </QuickCustomerSwitcherStyles>
    </ClickAwayListener>
  );
};

const OptionStyles = styled.li`
  width: 100%;
  display: flex;
  align-items: flex-start !important;
  flex-direction: column;
  .description {
    font-size: 0.8em;
    color: ${darkGrey};
    margin-top: -2px;
  }
`;
const QuickCustomerSwitcherStyles = styled.div<{ $isDarkMode: boolean }>`
  background: ${({ $isDarkMode }) =>
    $isDarkMode ? darkModeModalBackgroundColor : "white"};
  padding: 6px;
  border-radius: 5px;
  box-shadow: 1px 1px 20px 9px hsl(0deg 0% 0% / 32%);
  z-index: ${Z_INDEX.L11};
  position: fixed;
  width: ${WIDTH}px;
  max-width: ${BREAKPOINT_MOBILE - 64}px;
  height: ${HEIGHT}px;
  top: 10vh;
  left: calc(50vw - ${WIDTH / 2}px);
  .MuiAutocomplete-root,
  .MuiFormControl-root,
  .MuiInputBase-root {
    height: 100%;
  }
  .MuiInputBase-input {
    padding: 0 12px;
  }
  .MuiAutocomplete-option {
    overflow-wrap: anywhere;
  }
`;
export default QuickCustomerSwitcher;

function useCtrlKorBToToggle(
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
) {
  useEventListener("keydown", event => {
    if (event.ctrlKey && ["k", "b"].includes(event.key)) {
      event.preventDefault();
      setOpen(prev => !prev);
    }
  });
}

function useClickOnMountToOpen(active: boolean) {
  useEffect(() => {
    if (active) {
      const dropdownBtn = document.querySelector(
        ".MuiAutocomplete-popupIndicator"
      ) as HTMLElement;
      if (dropdownBtn) {
        dropdownBtn.click();
      }
    }
  }, [active]);
}
